import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  ChangeEvent,
} from "react";
import { useLazyGetParkingsQuery, useParkingApiPrefetch } from "services/parkingApi";
import { Company, Order, Parking, User } from "helpers";
import { ParkingResponse } from "services";
import { FilterItem } from "components/Table";
import { useUser } from "hooks";

interface Params {
  search?: string;
  sort?: string;
  order?: Order;
  status?: string;
  company?: string;
  municipality?: string;
}

const initialParams: Params = {
  search: undefined,
  sort: undefined,
  order: undefined,
  status: undefined,
};

export interface ParkingManagementContextValues {
  user: User | undefined;
  parkings: Parking[] | undefined;
  data: ParkingResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  params: Params;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  onSort: (sort: string, order?: Order) => void;
  onSearch: (search: string) => void;
  onFilterByStatus: (status?: FilterItem) => void;
  onResetFilters: () => void;
  onPrefetchNext: (page: number) => void;
  onFetch: (preferCacheValue?: boolean) => Promise<void>;
  onChangeMunicipality: (event: ChangeEvent<HTMLSelectElement>) => void;
  onChangeCompany: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export const ParkingManagementContext = createContext<ParkingManagementContextValues | undefined>(undefined);

export const ParkingManagementProvider = ({ children }: { children: ReactNode }) => {
  const { companyId, user, setCompanyId, resetCompany } = useUser();
  const [trigger, result] = useLazyGetParkingsQuery();
  const prefetchParkings = useParkingApiPrefetch("getParkings");
  const [params, setParams] = useState<Params>(initialParams);
  const [page, setPage] = useState<number>(0);
  const pageSize = 5;

  useEffect(() => {
    !!companyId && onFetchHandler(true);
  }, [companyId, params, page]);

  const onSortHandler = (sort: string, order?: Order) => {
    setPage(0);
    setParams((oldParams) => ({ ...oldParams, sort, order }));
  };

  const onFilterByCompanyHandler = (company?: string) => {
    setPage(0);
    setParams((oldParams) => ({
      ...oldParams,
      company: company || undefined,
    }));
  };

  const onSearchHandler = (search: string) => {
    setPage(0);
    setParams(() => ({ ...initialParams, search }));
  };

  const onFilterByStatusHandler = (status?: FilterItem) => {
    setPage(0);
    setParams((oldParams) => ({
      ...oldParams,
      status: status ? String(status) : undefined,
    }));
  };

  const onResetFiltersHandler = () => {
    setPage(0);
    setParams(initialParams);
  };

  const onPrefetchNextHandler = (page: number) => {
    if (!!result.data && page < result.data.totalPages)
      prefetchParkings(
        { company_id: companyId === "0" ? "" : companyId, page, pageSize, ...params },
        { ifOlderThan: 5 }
      );
  };

  const onFetchHandler = async (preferCacheValue: boolean = false) => {
    try {
      await trigger(
        {
          company_id: companyId === "0" ? "" : companyId,
          page,
          pageSize,
          ...params,
          municipality_id: params.municipality, // Send municipality value as municipality_id
        },
        preferCacheValue
      );
    } catch (error) {
      console.log(error);
    }
  };

  const onChangeCompanyHandler = (event: ChangeEvent<HTMLSelectElement> | { value: string; label: string }) => {
    setPage(0);
    const company_id = "target" in event ? event.target.value : event.value;
    setCompanyId(!!company_id ? company_id : "0");
    setParams(initialParams);
  };

  const onChangeMunicipalityHandler = (event: ChangeEvent<HTMLSelectElement> | { value: string }) => {
    setPage(0);
    const municipality_id = "target" in event ? event.target.value : event.value;
    setParams((oldParams) => ({
      ...oldParams,
      municipality: municipality_id?.trim() || undefined,
    }));
  };

  const values: ParkingManagementContextValues = {
    user,
    parkings: result?.data?.parkings,
    data: result?.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isSuccess: result.isSuccess,
    params,
    page,
    setPage,
    onSort: onSortHandler,
    onSearch: onSearchHandler,
    onFilterByStatus: onFilterByStatusHandler,
    onResetFilters: onResetFiltersHandler,
    onPrefetchNext: onPrefetchNextHandler,
    onFetch: onFetchHandler,
    onChangeMunicipality: onChangeMunicipalityHandler,
    onChangeCompany: onChangeCompanyHandler,
  };

  return <ParkingManagementContext.Provider value={values}>{children}</ParkingManagementContext.Provider>;
};

export const useParkingManagementContext = () => {
  const context = useContext(ParkingManagementContext);

  if (!context) {
    throw new Error("useParkingManagement must be used within a ParkingManagementProvider!");
  }
  return context;
};
